import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { RootLayout } from 'analysis-ui-components';
import { useEffect, useCallback, useReducer, useMemo, useRef, memo, forwardRef, useImperativeHandle, } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useToggle, useFullscreen } from 'react-use';
import { toJSON } from '../data/SpectraManager';
import checkModifierKeyActivated from '../data/utilities/checkModifierKeyActivated';
import Viewer1D from './1d/Viewer1D';
import Viewer2D from './2d/Viewer2D';
import ErrorOverlay from './ErrorOverlay';
import KeysListenerTracker from './EventsTrackers/KeysListenerTracker';
import { SplitPaneWrapper } from './SplitPaneWrapper';
import { AssignmentProvider } from './assignment';
import { ChartDataProvider } from './context/ChartContext';
import { DispatchProvider } from './context/DispatchContext';
import { GlobalProvider } from './context/GlobalContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { AlertProvider } from './elements/popup/Alert';
import { ModalProvider } from './elements/popup/Modal';
import Header from './header/Header';
import { HighlightProvider } from './highlight';
import DropZone from './loader/DropZone';
import { defaultGetSpinner, SpinnerProvider } from './loader/SpinnerContext';
import Panels from './panels/Panels';
import checkActionType from './reducer/IgnoreActions';
import { spectrumReducer, initialState, dispatchMiddleware, initState, } from './reducer/Reducer';
import { DISPLAYER_MODE } from './reducer/core/Constants';
import preferencesReducer, { preferencesInitialState, initPreferencesState, } from './reducer/preferences/preferencesReducer';
import { INITIATE, SET_LOADING_FLAG, SET_MOUSE_OVER_DISPLAYER, } from './reducer/types/Types';
import ToolBar from './toolbar/ToolBar';
import { getBlob } from './utility/export';
const viewerContainerStyle = css `
  border: 0.55px #e6e6e6 solid;
  display: flex;
  flex: 1;
  flex-direction: 'column';
  height: 100%;
  margin-left: -1px;
`;
const containerStyles = css `
  background-color: white;
  width: 100%;
  display: block;
  height: 100%;
  font-size: 14px;

  div:focus {
    outline: none !important;
  }

  button {
    cursor: pointer;
    &:disabled {
      cursor: default;
    }
  }

  button:active,
  button:hover,
  button:focus,
  [type='button']:focus,
  button {
    outline: none !important;
  }

  * {
    -webkit-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .SplitPane {
    height: 100%;
  }
`;
const defaultPreferences = {};
const defaultData = {
    spectra: [],
};
const NMRium = forwardRef(function NMRium(props, ref) {
    return (_jsx(RootLayout, { style: { width: '100%' }, children: _jsx(ErrorBoundary, { FallbackComponent: ErrorOverlay, children: _jsx(InnerNMRium, { ...props, innerRef: ref }) }) }));
});
function InnerNMRium({ data: dataProp = defaultData, workspace, preferences = defaultPreferences, getSpinner = defaultGetSpinner, onDataChange, onViewChange, emptyText, innerRef, }) {
    const rootRef = useRef(null);
    const elementsWrapperRef = useRef(null);
    const viewerRef = useRef(null);
    const [show, toggle] = useToggle(false);
    const handleDataChange = useRef(onDataChange);
    useEffect(() => {
        handleDataChange.current = onDataChange;
    }, [onDataChange]);
    const handleViewChange = useRef(onViewChange);
    useEffect(() => {
        handleViewChange.current = onViewChange;
    }, [onViewChange]);
    const isFullscreen = useFullscreen(rootRef, show, {
        onClose: () => {
            toggle(false);
        },
    });
    const [state, dispatch] = useReducer(spectrumReducer, initialState, initState);
    const [preferencesState, dispatchPreferences] = useReducer(preferencesReducer, preferencesInitialState, initPreferencesState);
    const { displayerMode, data: spectraData, actionType, view } = state;
    useEffect(() => {
        if (checkActionType(actionType)) {
            handleDataChange.current?.(toJSON(state, 'onDataChange'));
        }
    }, [actionType, state]);
    useEffect(() => {
        handleViewChange.current?.(view);
    }, [view]);
    const dispatchMiddleWare = useMemo(() => {
        return dispatchMiddleware(dispatch);
    }, []);
    useEffect(() => {
        rootRef.current?.focus();
    }, [isFullscreen]);
    useEffect(() => {
        dispatchPreferences({
            type: 'INIT_PREFERENCES',
            payload: {
                display: preferences,
                workspace,
                dispatch: dispatchPreferences,
            },
        });
    }, [preferences, workspace]);
    useImperativeHandle(innerRef, () => ({
        getSpectraViewerAsBlob: () => {
            return rootRef?.current ? getBlob(rootRef.current, 'nmrSVG') : null;
        },
    }), []);
    useEffect(() => {
        dispatchMiddleWare({ type: SET_LOADING_FLAG, isLoading: true });
        if (dataProp) {
            dispatchMiddleWare({ type: INITIATE, payload: dataProp });
        }
    }, [dataProp, dispatchMiddleWare]);
    const preventContextMenuHandler = useCallback((e) => {
        if (!checkModifierKeyActivated(e)) {
            e.preventDefault();
        }
    }, []);
    const mainDivRef = useRef(null);
    useEffect(() => {
        const div = mainDivRef.current;
        if (!div) {
            return;
        }
        function mouseEnterHandler() {
            dispatchMiddleWare({ type: SET_MOUSE_OVER_DISPLAYER, payload: true });
        }
        function mouseLeaveHandler() {
            dispatchMiddleWare({ type: SET_MOUSE_OVER_DISPLAYER, payload: false });
        }
        div.addEventListener('mouseenter', mouseEnterHandler);
        div.addEventListener('mouseleave', mouseLeaveHandler);
        return () => {
            div.removeEventListener('mouseenter', mouseEnterHandler);
            div.removeEventListener('mouseleave', mouseLeaveHandler);
        };
    }, [dispatchMiddleWare]);
    return (_jsx(GlobalProvider, { value: {
            rootRef: rootRef.current,
            elementsWrapperRef: elementsWrapperRef.current,
            viewerRef: viewerRef.current,
        }, children: _jsx(PreferencesProvider, { value: preferencesState, children: _jsx("div", { ref: mainDivRef, style: { height: '100%', position: 'relative' }, children: _jsx(AlertProvider, { wrapperRef: elementsWrapperRef.current, children: _jsx(DispatchProvider, { value: dispatchMiddleWare, children: _jsx(ChartDataProvider, { value: state, children: _jsx(ModalProvider, { wrapperRef: elementsWrapperRef.current, children: _jsx(HighlightProvider, { children: _jsx(AssignmentProvider, { spectraData: spectraData, children: _jsx(SpinnerProvider, { value: getSpinner, children: _jsx("div", { className: "nmrium-container", ref: rootRef, css: containerStyles, onContextMenu: preventContextMenuHandler, style: { height: '100%', width: '100%' }, children: _jsx(DropZone, { children: _jsxs("div", { style: {
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            backgroundColor: 'white',
                                                            width: '100%',
                                                        }, children: [_jsx(Header, { isFullscreen: isFullscreen, onMaximize: toggle }), _jsxs("div", { style: {
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    height: '100%',
                                                                }, children: [_jsx(ToolBar, {}), _jsxs(SplitPaneWrapper, { children: [_jsxs("div", { css: viewerContainerStyle, children: [_jsx(KeysListenerTracker, {}), _jsx("div", { "data-test-id": "viewer", ref: viewerRef, style: {
                                                                                            width: '100%',
                                                                                            height: '100%',
                                                                                        }, children: displayerMode ===
                                                                                            DISPLAYER_MODE.DM_1D ? (_jsx(Viewer1D, { emptyText: emptyText })) : (_jsx(Viewer2D, { emptyText: emptyText })) })] }), _jsx(Panels, {})] }), _jsx("div", { ref: elementsWrapperRef, id: "main-wrapper", style: {
                                                                            position: 'absolute',
                                                                            pointerEvents: 'none',
                                                                            zIndex: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            top: 0,
                                                                            bottom: 0,
                                                                        } }, String(isFullscreen))] })] }) }) }) }) }) }) }) }) }) }) }) }) }));
}
export default memo(NMRium);
